import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
const IMAGE_BASE_URL = "https://kumuduorderapp.blob.core.windows.net/804-jjj/";

const FilterInfo = () => {
  const { filterId } = useParams();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStock, setLoadingStock] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedStocks, setSelectedStocks] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchCustomers();
  }, [filterId]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/fetchorder/getCustomersByFilter/${filterId}`
      );
      setCustomers(response.data.customers);
      setLoading(false);
    } catch (error) {
      setError("Failed to load customers.");
      setLoading(false);
    }
  };

  const handleCustomerSelection = async (event) => {
    const customerPhone = event.target.value;
    setSelectedCustomer(customerPhone);
    setLoadingStock(true);
    setSelectedStocks([]);
    setSelectAll(false);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/fetchorder/getStockByCustomer/${filterId}/${customerPhone}`
      );
      setStockData(response.data.stockData || []);
      setLoadingStock(false);
    } catch (error) {
      setError("Failed to load stock data.");
      setLoadingStock(false);
    }
  };

  const handleStockSelection = (stock) => {
    setSelectedStocks((prev) =>
      prev.some((s) => s.tagno === stock.tagno)
        ? prev.filter((s) => s.tagno !== stock.tagno)
        : [...prev, stock]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedStocks([]);
    } else {
      const selectableStocks = stockData.filter((stock) => stock.flag === "S");
      setSelectedStocks(selectableStocks);
    }
    setSelectAll(!selectAll);
  };

  const handleCreateOrder = async () => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_BASE_URL}/api/orders/createExOrder`,
        {
          filterId,
          // Pin the order to the selected store so multi-account mobiles are
          // resolved to the correct MFR account on the backend.
          mfrId: JSON.parse(localStorage.getItem("selectedMfr"))?.id,
          customerDetails: customers.find(
            (c) => c.customerPhone === selectedCustomer
          ),
          selectedStocks,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setSnackbar({
        open: true,
        message: "Order created successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.data?.message,
        severity: "error",
      });
    }
  };

  if (loading)
    return <CircularProgress sx={{ display: "block", mx: "auto", my: 4 }} />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container maxWidth="lg">
      <FormControl fullWidth sx={{ my: 2 }}>
        <InputLabel>Select Customer</InputLabel>
        <Select
          value={selectedCustomer || ""}
          onChange={handleCustomerSelection}
        >
          {customers.map((customer) => (
            <MenuItem
              key={customer.customerPhone}
              value={customer.customerPhone}
            >
              {customer.customerName} ({customer.customerPhone})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loadingStock && (
        <CircularProgress sx={{ display: "block", mx: "auto", my: 2 }} />
      )}

      {stockData.length > 0 && (
        <FormControlLabel
          control={<Checkbox checked={selectAll} onChange={handleSelectAll} />}
          label="Select All Sold Stocks"
        />
      )}

      <Grid container spacing={3} mb={4}>
        {stockData.map((stock) => (
          <Grid item xs={6} sm={4} md={2} key={stock.tagno}>
            <Card
              sx={{
                boxShadow: 3,
                borderRadius: 3,
                p: 1,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              <CardMedia
                component="img"
                image={stock.imageUrl}
                alt="Stock Item"
                sx={{
                  width: "100%",
                  height: 200,
                  objectFit: "cover",
                  borderRadius: 2,
                }}
                onClick={() => setSelectedImage(stock.imageUrl)}
              />
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold">
                  {stock.itemtype_name}
                </Typography>
                <Typography variant="body2">
                  Tagno:{stock.tagno_alpha || stock.tagno}
                </Typography>
                <Typography variant="body2">
                  Gross Wt: {parseFloat(stock.gross).toFixed(3)}
                </Typography>
                <Typography variant="body2">
                  Net Wt: {parseFloat(stock.netwt).toFixed(3)}
                </Typography>
                {stock.flag === "S" && (
                  <Checkbox
                    checked={selectedStocks.some(
                      (s) => s.tagno === stock.tagno
                    )}
                    onChange={() => handleStockSelection(stock)}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {selectedStocks.length > 0 && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateOrder}
          sx={{ mb: 10 }}
        >
          Create Order
        </Button>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          '&.MuiSnackbar-root': {
            bottom: '100px !important', // force override snackbar position
          }
        }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FilterInfo;
